import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import { useUser } from './user-provider';
import currencyJson from '../files/json/currencies.json';
import timezonesJson from '../files/json/timezones.json';

// Types
interface Country {
    id: number;
    name: string;
    currency: string;
    dateformat: string;
    timezone: string;
}

interface Company {
    id: number;
    name: string;
    businessType?: string;
    countryId?: number;
    contactNumber?: string;
    createdAt?: string;
}

interface ProvisionMatchConfig {
    amountPoints: number;
    amountTolerance: number;
    sameDayPoints: number;
    within1DayPoints: number;
    within3DayPoints: number;
    within7DayPoints: number;
    within30DayPoints: number;
    descriptionTokenThresholds: Array<{ minTokens: number; score: number }>;
    categoryBasePoints: number;
    categoryBonusPoints: number;
    minimumThreshold: number;
}

interface LatestAutoProvision {
    status: string;
    elapsedTime: string;
    createdAt: string;
}

interface Misc {
    latestAutoProvision: LatestAutoProvision;
}

interface Settings {
    projectionHorizon: string;
    startDayOfWeek?: string;
    visibleHistory?: string;
    currencySymbol: string;
    dateformat: string;
    timezone: string;
    provisionMatchConfig?: ProvisionMatchConfig;
    consolidateOneTimeThreshold?: number;
}

interface CompanySettings {
    company: Company;
    country: Country;
    settings: Settings;
    misc?: Misc;
}

interface SettingsContextType {
    // Company Settings
    companySettings: CompanySettings | null;
    
    // Loading states
    isLoadingSettings: boolean;
    
    // Error states
    settingsError: string | null;
    
    // Refresh functions
    refreshSettings: () => void;
    
    // Utility functions
    formatDate: (date: Date | string, showTime?: boolean) => string;
    formatAmount?: (amount: number) => string;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

interface SettingsProviderProps {
    children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
    const { user } = useUser();
    const location = useLocation();
    
    // States
    const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
    const [settingsError, setSettingsError] = useState<string | null>(null);

    // API hooks
    const [fetchCompany, setFetchCompany] = useApi();

    // Refresh functions
    const refreshSettings = useCallback(() => {
        if (user?.entityId) {
            const { url, method } = buildApiRequestConfig(routes.companySettings.fetchById, [
                { param: 'id', value: user.entityId }
            ]);
            setFetchCompany({ url, method });
            setSettingsError(null);
        }
    }, [user?.entityId, setFetchCompany]);

    // Format date based on company settings or default, using timezone if available
    const formatDate = useCallback((date: Date | string, showTime: boolean = false): string => {
        const format = companySettings?.settings.dateformat || 'YYYY-MM-DD';
        const timezone = companySettings?.settings.timezone;
        const timezoneInfo = timezonesJson.find(tz => tz.value === timezone);

        let dateObj: Date = date instanceof Date ? date : new Date();
        let year: string | undefined, month: string | undefined, day: string | undefined;

        // Date objects and strings with an actual time part (e.g. Sequelize's
        // ISO createdAt) carry real time info; a bare "YYYY-MM-DD" string
        // doesn't, so extracting a time from it would just show a spurious,
        // timezone-shifted midnight.
        const hasTimeComponent = date instanceof Date || /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/.test(date);

        // Handle string dates in YYYY-MM-DD format without timezone conversion -
        // except a timestamp shown WITH its time (e.g. "Last ran ..."): that's
        // a real moment, so its date has to come from the company's timezone
        // like its time does (the branch below). Taking the date from the
        // text gave the UTC date - still "yesterday" in Sydney until 10-11am -
        // stitched onto the Sydney time, e.g. a run at 9:30am on the 27th
        // showing as "26 Oct 9:30".
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}($|\s|T)/.test(date) && !(showTime && hasTimeComponent)) {
            const parts = date.split('-');
            if (parts.length >= 3 && /^\d{2}$/.test(parts[2].substring(0, 2))) {
                year = parts[0];
                month = parts[1];
                day = parts[2].substring(0, 2);
                // Y/M/D above are taken straight from the string to avoid a
                // timezone shift, but if there's a time part, dateObj still
                // needs the real parsed value for the time extraction below.
                if (hasTimeComponent) dateObj = new Date(date);
            } else {
                // Fall through to Date object handling if parsing fails
                dateObj = new Date(date);
                if (isNaN(dateObj.getTime())) {
                    return '';
                }
                year = String(dateObj.getFullYear());
                month = String(dateObj.getMonth() + 1).padStart(2, '0');
                day = String(dateObj.getDate()).padStart(2, '0');
            }
        } else {
            // Convert to Date if input is string (for other formats)
            if (typeof date === 'string') {
                dateObj = new Date(date);
                if (isNaN(dateObj.getTime())) {
                    return '';
                }
            }

            // This carries a real instant in time (a Date object or a full
            // ISO timestamp, unlike the bare YYYY-MM-DD branch above) - the
            // date and time-of-day must come from the SAME timezone, or a
            // moment that rolls to the next day in the company's configured
            // timezone (e.g. 14:20 UTC -> 00:20 the next day in Sydney)
            // shows that later time-of-day stitched onto the browser's own
            // local calendar date instead, producing a timestamp that never
            // actually existed. Mirror the same Intl-based conversion the
            // time extraction below already uses, instead of the
            // browser-local getFullYear()/getMonth()/getDate().
            if (timezone && typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
                try {
                    const dateParts = new Intl.DateTimeFormat('en-US', {
                        timeZone: timezoneInfo?.utc?.[0] || 'UTC',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                    }).formatToParts(dateObj);
                    year = dateParts.find(p => p.type === 'year')?.value;
                    month = dateParts.find(p => p.type === 'month')?.value;
                    day = dateParts.find(p => p.type === 'day')?.value;
                } catch {
                    year = String(dateObj.getFullYear());
                    month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    day = String(dateObj.getDate()).padStart(2, '0');
                }
            } else {
                year = String(dateObj.getFullYear());
                month = String(dateObj.getMonth() + 1).padStart(2, '0');
                day = String(dateObj.getDate()).padStart(2, '0');
            }
        }

        if (!year || !month || !day) return '';


        // Get time if showTime is true and the value actually has a time component
        let timeStr = '';
        if (showTime && hasTimeComponent) {
            let hours: string | undefined, minutes: string | undefined, seconds: string | undefined;
            if (timezone && typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
                try {
                    const timeParts = new Intl.DateTimeFormat('en-US', {
                        timeZone: timezoneInfo?.utc?.[0] || 'UTC',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                    }).formatToParts(dateObj);

                    hours = timeParts.find(p => p.type === 'hour')?.value;
                    minutes = timeParts.find(p => p.type === 'minute')?.value;
                    seconds = timeParts.find(p => p.type === 'second')?.value;
                } catch {
                    hours = String(dateObj.getUTCHours()).padStart(2, '0');
                    minutes = String(dateObj.getUTCMinutes()).padStart(2, '0');
                    seconds = String(dateObj.getUTCSeconds()).padStart(2, '0');
                }
            } else {
                hours = String(dateObj.getHours()).padStart(2, '0');
                minutes = String(dateObj.getMinutes()).padStart(2, '0');
                seconds = String(dateObj.getSeconds()).padStart(2, '0');
            }
            timeStr = ` ${hours}:${minutes}:${seconds}`;
        }

        // Supported formats: DD MMM YYYY, MMM DD, YYYY, YYYY-MM-DD, MM/DD/YYYY, DD/MM/YYYY, DD-MM-YYYY
        const monthNames = [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];
        let result = '';
        switch (format) {
            case 'YYYY-MM-DD':
            result = `${year}-${month}-${day}${timeStr}`;
            break;
            case 'MM/DD/YYYY':
            result = `${month}/${day}/${year}${timeStr}`;
            break;
            case 'DD/MM/YYYY':
            result = `${day}/${month}/${year}${timeStr}`;
            break;
            case 'DD-MM-YYYY':
            result = `${day}-${month}-${year}${timeStr}`;
            break;
            case 'DD MMM YYYY':
            result = `${day} ${monthNames[Number(month) - 1]} ${year}${timeStr}`;
            break;
            case 'MMM DD, YYYY':
            result = `${monthNames[Number(month) - 1]} ${day}, ${year}${timeStr}`;
            break;
            default:
            result = `${year}-${month}-${day}${timeStr}`;
        }
        return result;
    }, [companySettings]);

    // Format amount based on company settings or default
    const formatAmount = useCallback((amount: number): string => {
        const currencySymbol = companySettings?.settings.currencySymbol || '$';
        // Use Intl.NumberFormat for proper thousands separator and decimals
        const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
        return `${currencySymbol} ${formatted}`;
    }, [companySettings]);

    // Fetch data when URL changes or user changes
    useEffect(() => {
        refreshSettings();
    }, [location.pathname, user?.entityId]);

    // Handle company settings response
    useEffect(() => {
        const { data, isLoading, error } = fetchCompany;
        if (data && !isLoading) {
            const settingsData: CompanySettings = {
                company: {
                    name: data.company.name || '',
                    id: data.company.id || 0 ,
                    businessType: data.company.businessType || undefined,
                    countryId: data.country.id || undefined,
                    contactNumber: data.company.contactNumber || undefined,
                    createdAt: data.company.createdAt || undefined,
                },
                settings: {
                    projectionHorizon: data.settings.projectionHorizon || '',
                    startDayOfWeek: data.settings.startDayOfWeek || '',
                    visibleHistory: data.settings.visibleHistory || '',
                    currencySymbol: currencyJson.find(c => c.name === data.country.currency)?.symbol || '$',
                    dateformat: data.country.dateformat || '',
                    timezone: data.country.timezone || '',
                    provisionMatchConfig: data.settings.provisionMatchConfig,
                    consolidateOneTimeThreshold: data.settings.consolidateOneTimeThreshold,
                },
                country: {
                    id: data.country.id,
                    name: data.country.name,
                    currency: data.country.currency,
                    dateformat: data.country.dateformat,
                    timezone: data.country.timezone
                },
                misc: data.misc ? {
                    latestAutoProvision: {
                        status: data.misc.latestAutoProvision?.status || '',
                        elapsedTime: data.misc.latestAutoProvision?.elapsedTime || '',
                        createdAt: data.misc.latestAutoProvision?.createdAt || ''
                    }
                } : undefined
            };
            setCompanySettings(settingsData);
            setSettingsError(null);
        }
        if (error) {
            setSettingsError('Failed to fetch company settings.');
        }
    }, [fetchCompany]);

    // Memoized so consumers depending on the whole context object don't see a
    // "changed" reference on every SettingsProvider render (formatDate/formatAmount/
    // refreshSettings are already stable useCallbacks, so this only recomputes
    // when one of the underlying values actually changes).
    const contextValue: SettingsContextType = useMemo(() => ({
        companySettings,
        isLoadingSettings: fetchCompany.isLoading,
        settingsError,
        refreshSettings,
        formatDate,
        formatAmount,
    }), [companySettings, fetchCompany.isLoading, settingsError, refreshSettings, formatDate, formatAmount]);

    return (
        <SettingsContext.Provider value={contextValue}>
            {children}
        </SettingsContext.Provider>
    );
};